import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import { ApiError } from '@/api/client.ts';
import type { Workspace } from '@/api/generated/schemas.ts';
import { useCreateWorkspace } from '@/api/generated/workspaces.ts';
import { zodResolver } from '@hookform/resolvers/zod';

import { termsFormDefaults, termsFormSchema } from './forms/TermsFormFields.tsx';
import { getWorkspaceFormDefaults, toWorkspaceInput, workspaceFormSchema } from './forms/WorkspaceFormFields.tsx';

const createWorkspaceFormSchema = workspaceFormSchema.merge(termsFormSchema);
type CreateWorkspaceFormValues = z.input<typeof createWorkspaceFormSchema>;
type CreateWorkspaceFormOutput = z.output<typeof createWorkspaceFormSchema>;

// Creating a workspace, in the setup and the workspace dialog, which its owner accepts the terms and the AVV for
// (plan task 57).
export const useCreateWorkspaceForm = (onCreated: (workspace: Workspace) => unknown) => {
    const createWorkspace = useCreateWorkspace();
    const form = useForm<CreateWorkspaceFormValues, unknown, CreateWorkspaceFormOutput>({
        resolver: zodResolver(createWorkspaceFormSchema),
        defaultValues: { ...getWorkspaceFormDefaults(null), ...termsFormDefaults },
    });

    const handleSubmit = async (values: CreateWorkspaceFormOutput) => {
        try {
            const data = { ...toWorkspaceInput(values), has_accepted_terms: values.has_accepted_terms };
            await onCreated(await createWorkspace.mutateAsync({ data }));
        } catch (error) {
            // Slugs are unique across all workspaces, which only the API can check.
            if (!(error instanceof ApiError && error.code === 'slug_taken')) throw error;
            form.setError('slug', { message: 'errors.api.slug_taken' }, { shouldFocus: true });
        }
    };

    return { form, handleSubmit };
};

export type CreateWorkspaceForm = ReturnType<typeof useCreateWorkspaceForm>;
